import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const defaultFabric = product.fabrics?.[0];

  const MAX_SWATCHES = 5;

  const getSortedImages = (fabric) => {
    return (fabric?.images || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
  };

  const initialImages = getSortedImages(defaultFabric);
  const initialMainImg =
    initialImages[0]?.imageURL ||
    defaultFabric?.displayImage ||
    "/assets/images/placeholder.jpg";
  const initialHoverImg = initialImages[1]?.imageURL || initialMainImg;
  const initialColor = defaultFabric?.fabricColour || '—';
  const initialPrice = defaultFabric?.defaultTotalPrice ?? 0;

  const [selectedFabric, setSelectedFabric] = useState(defaultFabric);
  const [mainImage, setMainImage] = useState(initialMainImg);
  const [hoverImage, setHoverImage] = useState(initialHoverImg);
  const [currentColor, setCurrentColor] = useState(initialColor);
  const [currentPrice, setCurrentPrice] = useState(initialPrice);

  const fabrics = product.fabrics || [];

  const { visibleFabrics, remainingCount } = useMemo(() => {
    const visible = fabrics.slice(0, MAX_SWATCHES);
    const remaining = Math.max(0, fabrics.length - MAX_SWATCHES);
    return { visibleFabrics: visible, remainingCount: remaining };
  }, [fabrics]);

  const applyFabricState = (fabric) => {
    const images = getSortedImages(fabric);
    const mainImg = images[0]?.imageURL || fabric?.displayImage || "/assets/images/placeholder.jpg";
    const hoverImg = images[1]?.imageURL || mainImg;
    const color = fabric?.fabricColour || '—';
    const price = fabric?.defaultTotalPrice ?? 0;

    setSelectedFabric(fabric);
    setMainImage(mainImg);
    setHoverImage(hoverImg);
    setCurrentColor(color);
    setCurrentPrice(price);
  };

  useEffect(() => {
    // Reset card preview whenever filtered fabrics change:
    // show first valid fabric image immediately (before hover).
    applyFabricState(defaultFabric);
  }, [product.productID, defaultFabric?.fabricID]);

  const handleSwatchHover = (fabric) => {
    applyFabricState(fabric);
  };

  const handleCardClick = () => {
    navigate(`/product/${product.productID}`);
  };

  const handleSwatchClick = (e, fabric) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/product/${product.productID}?fabricId=${fabric.fabricID}`);
  };

  return (
    <div className="col-xl-4 col-lg-4 col-md-6">
      <div
        className="shirt-card-link"
        style={{ textDecoration: 'none', cursor: 'pointer' }}
        onClick={handleCardClick}
      >
        <div className="shirt-card">
          <img
            src={mainImage}
            className="main-shirt-img"
            alt={product.productName}
            style={{
              width: '92%',
              maxWidth: '320px',
              height: '250px',
              objectFit: 'cover',
              margin: '0 auto',
              display: 'block'
            }}
            onMouseOver={() => setMainImage(hoverImage)}
            onMouseOut={() =>
              setMainImage(
                getSortedImages(selectedFabric)[0]?.imageURL ||
                  selectedFabric?.displayImage ||
                  "/assets/images/placeholder.jpg"
              )
            }
          />

          <h5>{product.productName}</h5>
          <p className="shirt-color-text">{currentColor}</p>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              flexWrap: 'nowrap',
              margin: '8px 0 10px'
            }}
          >
            {visibleFabrics.map((fabric, i) => (
              <span
                key={fabric.fabricID ?? i}
                className="color-option"
                style={{
                  backgroundImage: `url(${fabric.displayImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  width: '28px',
                  height: '24px',
                  borderRadius: '2px',
                  flex: '0 0 28px'
                }}
                onMouseOver={() => handleSwatchHover(fabric)}
                onClick={(e) => handleSwatchClick(e, fabric)}
              ></span>
            ))}

            {remainingCount > 0 && (
              <span
                className="color-more-count"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCardClick();
                }}
                style={{
                  cursor: 'pointer',
                  width: '28px',
                  height: '24px',
                  border: '1px solid #cfcfcf',
                  borderRadius: '2px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#444',
                  background: '#fff',
                  flex: '0 0 28px'
                }}
                title={`${remainingCount} more`}
              >
                +{remainingCount}
              </span>
            )}
          </div>

          <div className="price">
            <h5>${Number(currentPrice || 0).toFixed(2)}</h5>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
